/**
 * Sign in (v2 frame "Hub · Sign in"). No card and no glass: the form sits on the canvas,
 * centred, 360 wide. A verb for a title, one sentence, two fields, one primary. Passwords are the
 * only sign-in (Google was dropped 2026-11-02, Jonah).
 */
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Field } from '@buckmason/cx-ui';
import { safeNext, useAuth } from '../lib/auth';
import { useTitle } from '../lib/useTitle';

export function AuthFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="auth">
      <div className="wallpaper" aria-hidden="true" />
      <div className="auth-brand">Buck Mason</div>
      <div className="auth-column">{children}</div>
    </main>
  );
}

export function SignIn() {
  useTitle('Sign in');
  const { signIn } = useAuth();
  const nav = useNavigate();
  // From Set password, when the password was saved but signing in straight after did not work.
  const arrived = (useLocation().state ?? {}) as { passwordSet?: boolean; email?: string };
  const passwordSet = !!arrived.passwordSet;
  const [params] = useSearchParams();
  const [email, setEmail] = useState(arrived.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await signIn(email, password);
      const next = safeNext(params.get('next'), me);
      if (next && new URL(next).origin !== window.location.origin) window.location.assign(next);
      else nav(next ? new URL(next).pathname : '/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That email and password do not match.');
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      <form className="auth-column-form" onSubmit={submit}>
        <header>
          <h1>Sign in</h1>
          <p>
            <span className="auth-lede-wide">One sign-in for Quality Review, Academy and the hub.</span>
            <span className="auth-lede-narrow">One sign-in for every app.</span>
          </p>
        </header>
        {passwordSet && (
          <p className="auth-helper" role="status">
            Your password is set. Sign in with it.
          </p>
        )}

        <Field label="Email">
          <input type="email" autoComplete="username" required placeholder="you@buckmason.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>

        <Field label="Password" error={error ?? undefined}>
          <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>

        <button type="submit" className="primary btn-block" disabled={busy}>
          {busy ? 'Signing in…' : 'Continue'}
        </button>

        <p className="auth-helper">Need a link to set a password? Ask an admin.</p>
      </form>
    </AuthFrame>
  );
}
